import {
    Component, ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    OnChanges, OnDestroy,
    OnInit,
    Output, Renderer2, SimpleChanges,
    ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DocumentFile } from '@app/interfaces/dm/document';
import { SelectOption } from '@app/interfaces/sys/dict';
import { DocumentService } from '@services/dm/document.service';
import { KanbanBoardService } from '@services/lm/kanban-board.service';
import { AccountService } from '@services/sys/account.service';
import { ConfirmationService } from '@services/sys/confirmation.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { QuillEditorComponent } from 'ngx-quill';
import 'quill-mention/autoregister';

@Component({
    selector: 'app-input-content',
    templateUrl: './input-content.component.html',
    styleUrl: './input-content.component.less',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputContentComponent),
            multi: true,
        },
    ],
})
export class InputContentComponent implements OnInit, OnChanges, OnDestroy {
    @ViewChild('editor') editor!: QuillEditorComponent;
    @Input() content: string | undefined = '';
    @Input() editorId!: string;
    @Input() members: SelectOption[] = [];
    @Input() clickToEdit = false; // 点击后才编辑
    @Input() showAction = true; // 是否显示操作按钮
    @Input() required = false;
    @Input() formControlName = '';
    @Input() saveLoading = false;
    @Input() inputWarning = false;  // 输入是否校验给提示
    @Input() configId!: string;
    @Input() inactiveUsers: boolean = false;
    @Output() editSuccess = new EventEmitter();
    @Output() cancelEvent = new EventEmitter();

    lastValue: string | undefined = '';

    quillModules = {
        toolbar: {
            container: [
                ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
                ['blockquote'],
                [{ list: 'ordered' }, { list: 'bullet' }],                   // text direction
                [{ size: ['small', false, 'large', 'huge'] }],  // custom dropdown
                [{ color: [] }, { background: [] }],          // dropdown with defaults from theme
                ['link', 'image', 'video'],                      // link and image, video
            ],
            // handlers: {
            //     image: this.imageHandler
            // },
        },
        mention: {
            allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
            positioningStrategy: 'normal',
            mentionListClass: 'custom-mention-list',
            onSelect: (item: any, insertItem: any) => {
                const editor = this.editor!.quillEditor;
                insertItem(item);
                // necessary because quill-mention triggers changes as 'api' instead of 'user'
                editor.insertText(editor.getLength() - 1, '', 'user');
            },
            source: (searchTerm: any, renderList: any) => {
                const values = this.members.map(it => {
                    return {
                        id: it.value,
                        value: it.label
                    };
                });

                if (searchTerm.length === 0) {
                    renderList(values, searchTerm);
                } else {
                    const matches: any[] = [];

                    values.forEach((entry) => {
                        if (entry.value.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1) {
                            matches.push(entry);
                        }
                    });
                    renderList(matches, searchTerm);
                }
            }
        },
    };
    isEdit = false;
    editorValue = '';
    editorStyles = { height: '150px' };
    accountOptions: SelectOption[] = [];
    uploadFiles: {
        [imageId: string]: DocumentFile;
    } = {};
    displayValue!: SafeHtml;
    initEditor: any;
    addedFiles: string[] = [];
    timer: null | number = null;
    uploadingImageCount = 0;

    constructor(private sanitizer: DomSanitizer,
                private accountService: AccountService,
                private kanbanBoardService: KanbanBoardService,
                private documentService: DocumentService,
                private elementRef: ElementRef,
                private message: NzMessageService,
                private renderer: Renderer2,
                private confirmationService: ConfirmationService,) {
    }

    ngOnInit(): void {
        this.uploadingImageCount = 0;
        this.editorValue = this.content || '';
        this.renderer.listen(this.elementRef.nativeElement, 'paste', (event: ClipboardEvent) => {
            // 处理粘贴事件的逻辑，针对当前组件(避免一个页面使用多个当前组件时，粘贴事件执行多次)
            this.handlePaste(event);
        });
    }

    ngOnDestroy(): void {
        // 组件销毁时移除监听器
        this.renderer.removeAttribute(this.elementRef.nativeElement, 'paste');
    }

    ngOnChanges(changes: SimpleChanges): void {
        const { content } = changes;
        if (content) {
            this.displayValue = this.getSafeHtml(this.content || '');
            setTimeout(() => this.labelInactiveUsers());
        }
    }

    labelInactiveUsers() {
      const mentions = document.getElementsByClassName('mention');
      const mentionNodes = Array.from(mentions);
      mentionNodes.forEach(mention => {
        const mentionId = mention.getAttribute('data-id') || '';
        const isUserActive = this.members.some(member => member.value.toString() === mentionId);
        if (!isUserActive) {
          mention.setAttribute('data-inactive', 'true');
        } else {
          mention.removeAttribute('data-inactive');
        }
      });
    }

    handlePaste(event: ClipboardEvent): void {
        const clipboardData = event.clipboardData;
        if (clipboardData) {
            const items = clipboardData.items;
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (item.type.startsWith('image/')) {
                    const file = item.getAsFile();
                    if (file) {
                        this.uploadImage(file);
                    }
                }
            }
        } else {
            console.warn('Clipboard data is undefined or unsupported.');
        }
    }

    onChange: any = () => {
    }
    onTouch: () => void = () => null;

    writeValue(str: string): void {
        this.editorValue = str || '';
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouch = fn;
    }

    handleEdit(content?: string): void {
        this.isEdit = true;
        this.editorValue = content || (this.content || '');
        this.lastValue = this.editorValue;
        setTimeout(() => {
            this.initEditor.focus();
            this.initEditor.setSelection(this.editorValue.length, this.editorValue.length);
            this.labelInactiveUsers();
        }, 300);
    }

    handleSave(): void {
        if (this.required && !this.editorValue) {
            this.message.error('This field is required');
            return;
        }
        this.hideMentionList();
        this.confirmationService.confirmUpdateTask(this.inactiveUsers, () => {
            this.isEdit = false;
            const data = this.beforeSave();
            this.editSuccess.emit(data); // Emit the updated value
            this.editorValue = data.value;
            this.lastValue = this.editorValue; // Update lastValue to the new content
            }
        );
    }


    cancelEdit(): void {
        this.hideMentionList();
        this.isEdit = false;
        this.editorValue = this.lastValue || '';
        setTimeout(() => this.labelInactiveUsers());
        this.cancelEvent.emit();
    }

    hideMentionList(): void {
        // directly remove from DOM due to positioningStrategy: 'fixed' of mention list
        const editorElement = document.querySelector(`#${this.editorId}`);
        const mentionContainer = editorElement?.closest('.ql-container')?.querySelector('.ql-mention-list-container')
            || document.querySelector('.ql-mention-list-container');
        if (mentionContainer) {
            mentionContainer.remove();
        }
    }

    getSafeHtml(html: string): SafeHtml {
        return html ? this.sanitizer.bypassSecurityTrustHtml(html) : '';
    }

    /**
     * 监听内容变化
     */
    onContentChanged(event: any): void {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        this.labelInactiveUsers();
        this.timer = window.setTimeout(() => {
            this.formChange();
        }, 400);
    }

    /**
     * form表单字段传出
     */
    formChange(): void {
        if (!this.formControlName) {
            return;
        }
        if (this.editorValue && this.inputWarning && !this.configId) {
            this.beforeInputWarn();
            return;
        }
        const {value, newFiles} = this.beforeSave();
        this.addedFiles = newFiles;
        this.onChange(value);
    }

    beforeInputWarn(): void {
        this.message.warning('Please select the task type first.');
        this.editorValue = '';
        this.onChange('');
    }

    uploadImage(file: File | undefined, insert: boolean = false): void {
        if (!file) {
            return;
        }
        if (this.inputWarning && !this.configId) {
            if (insert) {
                this.beforeInputWarn();
            }
            return;
        }
        const formData = new FormData();
        formData.append('file', file);

        this.uploadingImageCount++;
        this.documentService.upload(formData).subscribe(
            (response: any) => {
                const fileData: DocumentFile = response.body;
                if (!fileData) {
                    return;
                }
                const { id, name, type, size } = fileData;
                const fileId = `id_${id}`;

                // 获取 Quill 编辑器实例
                const quill = this.editor.quillEditor; // 如果 Quill 引用不同，请调整
                const range = quill.getSelection();
                const tmp = document.querySelector(`#${this.editorId}`)?.querySelector('.ql-editor');

                if (type.startsWith('image/')) {
                    // 如果是图片文件
                    let imageEl: any;
                    if (!insert) {
                        const imgNodeList = tmp?.querySelectorAll('img') || [];
                        // 找到第一个没有 id 的 Base64 格式图片元素
                        imageEl = Array.from(imgNodeList).find(
                            (image) => image.src.startsWith('data:image') && !image.id
                        );
                    } else {
                        const imageUrl = this.documentService.getPath(id);
                        this.initEditor.insertEmbed(range?.index, 'image', imageUrl);
                        const imgNodeList = tmp?.querySelectorAll('img') || [];
                        // 找到第一个 id 匹配的图片元素
                        imageEl = Array.from(imgNodeList).find(
                            (image) => image.src.includes(id) && !image.id
                        );
                    }
                    if (imageEl) {
                        imageEl.id = fileId;
                    }
                } else {
                    const fileUrl = this.documentService.getPath(id);
                    const anchorTag = `<a href="${fileUrl}" download="${name}" id="${fileId}">${name}</a><br>`;
                    if (range) {
                        quill.clipboard.dangerouslyPasteHTML(range.index, anchorTag);
                        quill.insertText(range.index + anchorTag.length, '\n'); // 添加换行符
                    } else {
                        quill.clipboard.dangerouslyPasteHTML(anchorTag);
                        const currentLength = quill.getLength();
                        quill.insertText(currentLength, '\n'); // 添加换行符
                    }
                }

                this.uploadFiles = {
                    ...this.uploadFiles,
                    [fileId]: fileData,
                };
                this.formChange();

                if (range) {
                    const newCursorPosition = quill.getLength();
                    quill.setSelection(newCursorPosition, 0);
                } else {
                    console.warn('Quill selection is undefined.');
                }
            },
            (error) => {
                console.error('Error uploading file:', error);
            },
            () => {
                this.uploadingImageCount--;
            }
        );
    }

    onEditorCreated(editor: any): void {
        this.initEditor = editor;
        editor.getModule('toolbar').addHandler('image', () => {
            const fileInput = document.createElement('input');
            fileInput.setAttribute('type', 'file');
            fileInput.setAttribute('accept', '*');
            fileInput.addEventListener('change', () => {
                const file = fileInput.files?.[0];
                this.uploadImage(file, true);
            });
            fileInput.click();
        });
        setTimeout(() => this.labelInactiveUsers());
    }

    /** 替换之前将base64格式的全部进行替换 */
    /** needNewFiles代表是否上传到固定字段Attachment上 */
    beforeSave(): {
        value: string,
        newFiles: string[] // Ensure this is an array of DocumentFile objects
    } {
        const newFiles: DocumentFile[] = [];
        const tmp = document.querySelector(`#${this.editorId}`)?.querySelector('.ql-editor');
        const uploadFileIds = Object.keys(this.uploadFiles);
        const fileIds: string[] = [];

        // Handle images
        uploadFileIds.forEach(fileId => {
            const fileData = this.uploadFiles[fileId];
            const targetImg: any = tmp?.querySelector('#' + fileId);

            // Check if the file is an image by its MIME type
            if (fileData.type.startsWith('image/')) {
                if (!targetImg) {
                    return;
                }
                newFiles.push(fileData); // Add image file
                fileIds.push(fileData.id);
                targetImg.src = this.documentService.getPath(fileData.id);
            }
        });

        // Handle non-image files (files that are not images)
        uploadFileIds.forEach(fileId => {
            const fileData = this.uploadFiles[fileId];

            // Check if the file is not an image by its MIME type
            if (!fileData.type.startsWith('image/')) {
                newFiles.push(fileData); // Add non-image file
                fileIds.push(fileData.id);
            }
        });

        const innerHTML = tmp?.innerHTML as string;
        const html = (innerHTML || '').replace(/<p>|<\/p>|<br\s*\/?>/gi, '').trim(); // Clean up HTML
        const value = !html ? '' : innerHTML;
        this.uploadingImageCount = 0;

        return {
            value,
            newFiles: this.kanbanBoardService.formatFileList(newFiles) // Ensure newFiles is properly formatted
        };
    }
}
